import "./AssetField.scss";

import clsx from "clsx";
import { get } from "lodash-es";
import {
  FC,
  ReactNode,
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Doc } from "sharedb/lib/client";
import { twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import type {
  AssetFieldType,
  PathType,
  PdfState,
  VideoState,
  WorksheetType,
} from "../../../shared/types/worksheet";
import { deleteBlockFromWorksheet } from "../../../shared/worksheets/doc-helpers";

import { ImageAsset } from "./ImageAsset";
import { PdfPageToolbar } from "./PdfPageToolbar";
import VideoAsset from "./VideoAsset";
import { ZoomToolbar } from "./ZoomToolbar";

import VideoTracksForm from "components/forms/VideoTracksForm";
import { t } from "i18n/i18n";
import { CloseButton } from "mds/components/CloseButton";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem, MenuRadioGroup, SubMenu } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { TextArea } from "mds/components/TextArea";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  ArrowDownIcon,
  DotMenuVerticalIcon,
  ExpandFullScreenIcon,
  EyeVisibleIcon,
  LoadingDotsIcon,
  TrashDeleteIcon,
  WhiteboardIcon,
} from "mds/icons";
import { PaperClipLinkIcon } from "mds/icons/PaperClipLink";
import { DocViewContext } from "providers/DocViewProvider";
import { ShareDBContext } from "providers/ShareDBProvider";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectFeaturingPdfState, selectIsEditModeEnabled } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import {
  clearPdfStateOnDoc,
  clearVideoStateOnDoc,
  getDefaultPdfState,
  getDefaultVideoState,
  setPdfStateOnDoc,
  setVideoStateOnDoc,
} from "utils/asset";
import { useAv } from "utils/av";
import { usePresentation } from "utils/presentation";
import { Link } from "utils/routing";
import { isAccessibilityUIEnabled } from "utils/waffle-flags";
import { isImageFile, isPDFFile, isVideoFile } from "worksheets/helpers/files";

const PdfAsset = lazy(() => import("./PdfAsset"));

type AssetModalProps = {
  name: string;
  size: string;
  blockId: string;
  url: string;
  alt?: string;
  tracks?: AssetFieldType["tracks"];
  canAuthorPage: boolean;
  isImage: boolean;
  isVideo: boolean;
  isPdf: boolean;
  annotations: boolean;
  fieldId: string;
  onClose: (manualClose?: boolean) => void;
  onDelete: () => void;
};

const AssetModal: FC<AssetModalProps> = ({
  name,
  size,
  url,
  alt,
  tracks,
  blockId,
  canAuthorPage,
  isImage,
  isVideo,
  isPdf,
  annotations,
  fieldId,
  onClose,
  onDelete,
}) => {
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  const [pdfState, setPdfState] = useState<PdfState>(getDefaultPdfState());

  const onPdfStateChange = (newState: Partial<PdfState>) => {
    setPdfState((prevState) => ({ ...prevState, ...newState }));
  };

  return (
    <>
      <div
        className={clsx(
          "flex w-full min-w-0 items-center justify-between gap-2 rounded-t-lg bg-black-tint-95 p-2",
          isPdf && "max-sm:flex-col max-sm:items-stretch",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <AssetTitle className="h4 gap-1" name={name} size={size} />
          {isPdf && <CloseButton className="shrink-0 sm:hidden" onClick={() => onClose()} />}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {isPdf && (
            <>
              <ZoomToolbar setZoom={(zoom) => onPdfStateChange({ zoom })} zoom={pdfState.zoom} />
              <PdfPageToolbar pdfState={pdfState} setPdfState={onPdfStateChange} />
            </>
          )}

          {(!canAuthorPage || !isEditModeEnabled) && (
            <SquareIconButton as={Link} download={name} kind="tertiary" size="s" to={url}>
              <ArrowDownIcon />
            </SquareIconButton>
          )}

          {canAuthorPage && isEditModeEnabled && (
            <InstructorOptionsMenu name={name} url={url} onDelete={onDelete} />
          )}

          <CloseButton className={clsx(isPdf && "hidden sm:flex")} onClick={() => onClose()} />
        </div>
      </div>

      {isImage && (
        <ImageAsset
          alt={alt}
          annotations={annotations}
          blockId={blockId}
          fieldId={fieldId}
          url={url}
          isInModal
        />
      )}

      {isVideo && (
        <VideoAsset className="enlarged-preview__media" id={blockId} tracks={tracks} url={url} />
      )}

      {isPdf && (
        <Suspense fallback={<LoadingDotsIcon />}>
          <PdfAsset
            annotations={annotations}
            blockId={blockId}
            className="enlarged-preview__media"
            fieldId={fieldId}
            pdfState={pdfState}
            setPdfState={onPdfStateChange}
            url={url}
            isInModal
          />
        </Suspense>
      )}
    </>
  );
};

interface AssetFieldProps extends AssetFieldType {
  path: PathType;
  doc: Doc<WorksheetType>;
  blockId: string;
  blockHeight: number;
}

export const AssetField: FC<AssetFieldProps> = ({ id, path, doc, blockId, blockHeight }) => {
  const {
    name,
    size,
    url,
    fileType,
    view,
    annotations,
    alt,
    tracks,
    pdfState: presentingPdfState,
    videoState: presentingVideoState,
  } = get(doc?.data, path) as AssetFieldType;
  const { canAuthorPage, isProjecting, isFeaturing } = useContext(DocViewContext);
  const { presentedBlockId, isFollowing, isPresenting } = usePresentation();
  const featuringPdfState = useAppSelector(selectFeaturingPdfState);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const docContext = useContext(DocViewContext);
  const shareDBContextValue = useContext(ShareDBContext);
  const dispatch = useAppDispatch();
  const presenterUpdatedAssetState = useRef(false);
  const { isInVirtualClass } = useAv();

  const isFeaturedAsset = isFeaturing && presentedBlockId === blockId;

  // These are for PDFs only
  const [assetPdfState, setAssetPdfState] = useState<PdfState>(
    isProjecting || isFollowing ? presentingPdfState || getDefaultPdfState() : getDefaultPdfState(),
  );
  // These are for images only
  const { value: altTextFormIsShown, toggle: toggleAltTextForm } = useBoolean(false);
  // These are for videos only
  const {
    value: tracksFormIsShown,
    toggle: toggleTracksForm,
    setFalse: closeTracksForm,
  } = useBoolean(false);

  const isInline = view === "inline";
  const isCondensed = view === "condensed";

  const isImage = !!url && isImageFile(fileType);
  const isVideo = !!url && isVideoFile(fileType);
  const isPdf = !!url && isPDFFile(fileType);
  const isFollowingAsset = isFollowing && (isPdf || isInVirtualClass);
  const shouldSaveAssetStateInDoc = isPresenting && (isPdf || isInVirtualClass);
  const previousPdfState = useRef<PdfState | null>(
    presentingPdfState ? { ...presentingPdfState } : null,
  );
  const hasThumbnail = (isImage || isVideo) && isCondensed;
  const canPreview = isImage || isVideo || isPdf;
  const userOverridingPdfAssetState = Boolean(
    isFeaturedAsset ? featuringPdfState?.userUpdated : assetPdfState?.userUpdated,
  );

  const showAnnotationsOptions = isPdf || isImage;

  const onAnnotationsChange = () => {
    doc.submitOp([{ p: path.concat("annotations"), oi: !annotations, od: annotations }]);
  };

  const onDelete = useCallback(() => {
    const blockIndex = Number(path[1]);
    deleteBlockFromWorksheet(doc, blockIndex);
  }, [doc, path]);

  const onViewChange = (newView: AssetFieldType["view"]) => {
    doc.submitOp([{ p: path.concat("view"), oi: newView, od: view }]);
  };

  const onAltTextChange = (newAlt: string | undefined) => {
    doc.submitOp([{ p: path.concat("alt"), oi: newAlt, od: alt }]);
  };

  const onTracksChange = (newTracks: AssetFieldType["tracks"]) => {
    doc.submitOp([{ p: path.concat("tracks"), oi: newTracks, od: tracks }]);
  };

  // If the asset is pdf and we're presenting we need to default the assetState
  const getPdfState = () => {
    if (shouldSaveAssetStateInDoc) {
      return presentingPdfState || getDefaultPdfState();
    }

    if (isFeaturedAsset) {
      return featuringPdfState;
    }

    return assetPdfState;
  };

  // If we change the zoom or page of the pdf we either update the copy here or save it
  // in the doc if we're presenting so that others can sync with our changes
  const setPdfState = (pdfState: Partial<PdfState>) => {
    if (shouldSaveAssetStateInDoc) {
      setPdfStateOnDoc(doc, path, getPdfState(), pdfState);
      presenterUpdatedAssetState.current = true;
    } else if (isFeaturedAsset) {
      dispatch(viewStateActions.setFeaturingPdfState(pdfState));
    } else {
      setAssetPdfState({ ...assetPdfState, ...pdfState });
    }
  };

  // We get the video state from the doc if we're presenting or following, but we only
  // need to default it if we're presenting.
  const getVideoState = () => {
    if (shouldSaveAssetStateInDoc) {
      return presentingVideoState || getDefaultVideoState();
    }

    if (isFollowingAsset) {
      return presentingVideoState;
    }

    return undefined;
  };

  // As the video controls live with the video asset (unlike the pdf controls)
  // we don't need to save state outside of the video asset.  But if we're
  // presenting we want to save it to the doc for others to sync with.
  const setVideoState = (videoState: Partial<VideoState>) => {
    if (shouldSaveAssetStateInDoc) {
      setVideoStateOnDoc(doc, path, getVideoState(), videoState);
      presenterUpdatedAssetState.current = true;
    }
  };

  // Check for instructor change to pdfstate if we're following
  useEffect(() => {
    if (isFollowingAsset && presentingPdfState) {
      // If the user has diverged from the presenting state we only force them back if the page changes
      if (
        userOverridingPdfAssetState &&
        previousPdfState.current?.pageNumber === presentingPdfState.pageNumber
      ) {
        return;
      }

      previousPdfState.current = { ...presentingPdfState };
      if (isFeaturedAsset) {
        dispatch(
          viewStateActions.setFeaturingPdfState({ ...presentingPdfState, userUpdated: false }),
        );
      } else {
        setAssetPdfState({ ...presentingPdfState, userUpdated: false });
      }
    }
  }, [
    isFollowingAsset,
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
    presentingPdfState,
    isFeaturedAsset,
    dispatch,
    userOverridingPdfAssetState,
  ]);

  // Clear asset state when we stop presenting or unmount
  useEffect(() => {
    if (!isPresenting && presenterUpdatedAssetState.current) {
      if (isPdf) {
        clearPdfStateOnDoc(doc, path);
      } else if (isVideo) {
        clearVideoStateOnDoc(doc, path);
      }

      presenterUpdatedAssetState.current = false; // Only do this once.
    }

    return () => {
      if (presenterUpdatedAssetState.current) {
        if (isPdf) {
          clearPdfStateOnDoc(doc, path);
        } else {
          clearVideoStateOnDoc(doc, path);
        }

        presenterUpdatedAssetState.current = false; // Only do this once.
      }
    };
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [isPresenting, doc, path, isPdf, isVideo]);

  const onShowModal = useCallback(
    async () => {
      const onFeatureClose = () => dispatch(viewStateActions.setAssetModalIsOpen(false));

      await showConfirmationModal({
        hideCloseButton: true,
        className:
          "modal-preview bg-white !p-0 max-h-screen max-w-[100vw] overflow-hidden flex flex-col",
        hideCancelConfirmButtons: true,
        fullScreen: true,
        allowManualClose: true,
        onClose: onFeatureClose,
        // ShareDBContext is required for the WhiteboardField, which we need to pass down to the Modal
        shareDBContextValue,
        // This modal will not be getting updated so this nesting issue is not a problem
        // eslint-disable-next-line react/no-unstable-nested-components
        children: (onClose: () => void) => {
          if (!url) {
            return null;
          }

          return (
            // DocViewContext is required for the WhiteboardField, so we need to add it here
            // Note: this DocViewContext will not have the correct value for isFeaturing
            <DocViewContext.Provider value={docContext}>
              <AssetModal
                alt={alt}
                annotations={Boolean(annotations)}
                blockId={blockId}
                canAuthorPage={canAuthorPage}
                fieldId={id}
                isImage={isImage}
                isPdf={isPdf}
                isVideo={isVideo}
                name={name}
                size={size}
                tracks={tracks}
                url={url}
                onClose={onClose}
                onDelete={onDelete}
              />
            </DocViewContext.Provider>
          );
        },
      });

      return null;
    },
    // TODO: look into simplifying this list
    [
      canAuthorPage,
      isImage,
      isPdf,
      blockId,
      isVideo,
      dispatch,
      name,
      size,
      url,
      alt,
      tracks,
      onDelete,
      annotations,
      docContext,
      id,
      shareDBContextValue,
    ],
  );

  const onView = () => {
    dispatch(viewStateActions.setAssetModalIsOpen(true));
    onShowModal().catch(toastLocalizedOperationCatcher("preview_failure"));
  };

  if (canPreview && isInline) {
    return (
      <div className="w-full">
        <div
          className="inline-preview group relative w-full overflow-hidden rounded-md"
          role="toolbar"
        >
          {/* When being featured, the FeatureModal handles the header */}
          {!isFeaturedAsset && (
            <div
              className={clsx(
                "inline-preview__toolbar absolute left-0 top-0 z-10 flex w-full flex-col items-start gap-2 bg-black-tint-95/80 p-2",
                "invisible group-hover:visible has-[[data-stay-visible]]:visible",
                isPdf && "max-sm:flex-col max-sm:items-stretch",
              )}
            >
              <div className="flex w-full grow flex-row items-stretch justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <AssetTitle
                    className="h4 gap-1"
                    name={name}
                    showIcon={!hasThumbnail}
                    size={size}
                  />
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {isImage && canAuthorPage && isEditModeEnabled && isAccessibilityUIEnabled() && (
                    <SquareButton
                      id={`${id}-alt-text-button`}
                      kind="tertiary"
                      size="s"
                      onClick={toggleAltTextForm}
                    >
                      {t("asset_field.alt_text")}
                    </SquareButton>
                  )}
                  {isVideo && canAuthorPage && isEditModeEnabled && isAccessibilityUIEnabled() && (
                    <SquareButton kind="tertiary" size="s" onClick={toggleTracksForm}>
                      {t("asset_field.tracks.title")}
                    </SquareButton>
                  )}

                  {isPdf && (
                    <>
                      <ZoomToolbar
                        setZoom={(zoom) => setPdfState({ zoom, userUpdated: isFollowing })}
                        zoom={getPdfState().zoom}
                      />

                      <PdfPageToolbar
                        pdfState={getPdfState()}
                        setPdfState={(state) => setPdfState({ ...state, userUpdated: isFollowing })}
                      />
                    </>
                  )}

                  {(!canAuthorPage || !isEditModeEnabled) && (
                    <SquareIconButton as={Link} download={name} kind="tertiary" size="s" to={url}>
                      <ArrowDownIcon />
                    </SquareIconButton>
                  )}

                  <ViewButton onView={onView} />

                  {canAuthorPage && isEditModeEnabled && (
                    <InstructorOptionsMenu
                      annotations={Boolean(annotations)}
                      name={name}
                      showAnnotationsOptions={showAnnotationsOptions}
                      url={url}
                      view={view}
                      showViewOptions
                      onAnnotationsChange={onAnnotationsChange}
                      onDelete={onDelete}
                      onViewChange={onViewChange}
                    />
                  )}
                </div>
              </div>
              {altTextFormIsShown && (
                <form className="w-full" onSubmit={(e) => e.preventDefault()}>
                  <TextArea
                    aria-labelledby={`${id}-alt-text-button`}
                    className="w-full"
                    name="alt"
                    value={alt}
                    onInput={(e) => {
                      const value = e.currentTarget.value;
                      onAltTextChange(value || undefined);
                    }}
                  />
                </form>
              )}
              {tracksFormIsShown && (
                <VideoTracksForm
                  className="w-full overflow-auto"
                  defaultTracks={tracks}
                  onSubmit={(data) => {
                    onTracksChange(data.tracks);
                    closeTracksForm();
                  }}
                />
              )}
            </div>
          )}

          {isImage && (
            <ImageAsset
              alt={alt}
              annotations={annotations}
              blockHeight={blockHeight}
              blockId={blockId}
              fieldId={id}
              isFeaturingAsset={isFeaturedAsset}
              url={url}
            />
          )}

          {isVideo && (
            <VideoAsset
              blockHeight={blockHeight}
              className="inline-preview__media"
              id={blockId}
              setVideoState={setVideoState}
              shouldSyncWithInstructorView={isProjecting}
              tracks={tracks}
              url={url}
              videoState={getVideoState()}
            />
          )}

          {isPdf && (
            <Suspense fallback={<LoadingDotsIcon />}>
              <PdfAsset
                annotations={annotations}
                blockId={blockId}
                className="inline-preview__media"
                fieldId={id}
                isFeaturingAsset={isFeaturedAsset}
                pdfState={getPdfState()}
                setPdfState={setPdfState}
                shouldSyncWithInstructorView={isProjecting}
                url={url}
              />
            </Suspense>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx("flex-start flex w-full", hasThumbnail && "-with-thumbnail")}>
      {hasThumbnail && (
        <div className="preview-container h-16 w-24 shrink-0 overflow-hidden rounded-l-md">
          {isImage && <img alt={alt} className="h-full w-full object-cover" src={url} />}

          {isVideo && <video className="h-full w-full object-cover" src={url} controls />}
        </div>
      )}

      <div className="body-s flex min-w-0 flex-1 items-center justify-between gap-2 p-3">
        <AssetTitle className="h4 gap-1" name={name} showIcon={!hasThumbnail} size={size} />

        <div className="flex items-center gap-2">
          {canPreview ? (
            <>
              <SquareIconButton as={Link} download={name} kind="tertiary" size="s" to={url}>
                <ArrowDownIcon />
              </SquareIconButton>

              <ViewButton onView={onView} />
            </>
          ) : url ? (
            <SquareButton as={Link} download={name} kind="tertiary" size="s" to={url}>
              <ArrowDownIcon />
              {t("common.download")}
            </SquareButton>
          ) : null}

          {canAuthorPage && (
            <Menu
              menuButton={
                <SquareIconButton kind="ghost" size="m">
                  <DotMenuVerticalIcon />
                </SquareIconButton>
              }
            >
              {canPreview && canAuthorPage && (
                <ViewOptionsMenu value={view} onRadioChange={onViewChange} />
              )}

              {canAuthorPage && (
                <MenuItem kind="destructive" onClick={onDelete}>
                  <TrashDeleteIcon /> {t("common.delete")}
                </MenuItem>
              )}
            </Menu>
          )}
        </div>
      </div>
    </div>
  );
};

type ViewButtonProps = {
  onView: () => void;
};

export const ViewButton: FC<ViewButtonProps> = ({ onView }) => {
  const { isPresenting } = usePresentation();

  if (isPresenting) return null;

  return (
    <SquareButton kind="tertiary" size="s" onClick={onView}>
      <IconText iconStart={<ExpandFullScreenIcon />} text={t("asset_field.view")} hideTextOnSm />
    </SquareButton>
  );
};

type ViewOptionsMenuProps = {
  value: AssetFieldType["view"];
  onRadioChange: (newView: AssetFieldType["view"]) => void;
};

export const ViewOptionsMenu: FC<ViewOptionsMenuProps> = ({ value, onRadioChange }) => {
  return (
    <SubMenu
      className="gap-2"
      label={
        <>
          {/* TODO: Replace with different "eye" icon once it is added to the icon list in Figma */}
          <EyeVisibleIcon />
          {/* TODO: Is there a better way to localize SubMenu? We probably want Display As Condensed Preview or
           *   Display As Inline Preview rather than three substrings: Display As, Inline Preview, and
           *   Condensed Preview */}
          {t("asset_field.display_as")}
        </>
      }
      withArrow
    >
      <MenuRadioGroup
        value={value}
        onRadioChange={(e) => onRadioChange(e.value as AssetFieldType["view"])}
      >
        <MenuItem kind="radio" type="radio" value="inline">
          {t("asset_field.inline_preview")}
        </MenuItem>
        <MenuItem kind="radio" type="radio" value="condensed">
          {t("asset_field.condensed_preview")}
        </MenuItem>
      </MenuRadioGroup>
    </SubMenu>
  );
};

type InstructorOptionsMenuProps = {
  name: string;
  url: string;
  annotations?: boolean;
  view?: AssetFieldType["view"];
  showAnnotationsOptions?: boolean;
  showViewOptions?: boolean;
  onAnnotationsChange?: () => void;
  onDelete: () => void;
  onViewChange?: (newView: AssetFieldType["view"]) => void;
};

export const InstructorOptionsMenu: FC<InstructorOptionsMenuProps> = ({
  name,
  url,
  annotations,
  view,
  showAnnotationsOptions = false,
  showViewOptions = false,
  onAnnotationsChange,
  onDelete,
  onViewChange,
}) => {
  return (
    <Menu
      menuButton={
        <SquareIconButton kind="ghost" size="s">
          <DotMenuVerticalIcon />
        </SquareIconButton>
      }
    >
      <MenuItem download={name} href={url} allowPropagation>
        <ArrowDownIcon /> {t("common.download")}
      </MenuItem>
      {showViewOptions && view && onViewChange && (
        <ViewOptionsMenu value={view} onRadioChange={onViewChange} />
      )}
      {showAnnotationsOptions && annotations ? (
        <MenuItem kind="destructive" onClick={onAnnotationsChange}>
          <WhiteboardIcon /> {t("common.disable_annotations")}
        </MenuItem>
      ) : showAnnotationsOptions && !annotations ? (
        <MenuItem onClick={onAnnotationsChange}>
          <WhiteboardIcon /> {t("common.enable_annotations")}
        </MenuItem>
      ) : null}
      <MenuItem kind="destructive" onClick={onDelete}>
        <TrashDeleteIcon /> {t("common.delete")}
      </MenuItem>
    </Menu>
  );
};

type AssetTitleProps = {
  name: string;
  size: string;
  className?: string;
  icon?: ReactNode;
  showIcon?: boolean;
};

export const AssetTitle: FC<AssetTitleProps> = ({
  name,
  size,
  className,
  icon = <PaperClipLinkIcon />,
  showIcon = true,
}) => {
  return (
    <IconText
      className={twMerge("title min-w-0 flex-1", className)}
      iconStart={showIcon ? icon : undefined}
      text={
        <>
          <span className="min-w-0 truncate">{name}</span>
          <span className="body-s whitespace-nowrap text-black-tint-70">{`(${size})`}</span>
        </>
      }
    />
  );
};
